import React, { createRef } from 'react';

import clsx from 'classnames';
import canUse from '@/utils/canUse';

import { throttle, setTransform } from '@/utils/tools';
import { observer, inject } from 'mobx-react';
import { getChildInject } from '@/utils/instantLoaded/tools';
import { applyChatbotConfig } from '@/config';
import { isIosReg } from '@/utils/constants';
import { throttle as throttleReplacement } from '@/utils/lodashReplacement';

import './style.scss';
import { isShortCutNewUI } from '@/utils/experiment/getShortCutUIUXExperiment';

// 默认 loading 高度
const DEFAULT_LOADING_DISTANCE = 40;
// 节流频率
const THROTTLE_INTERVAL = 16;
// 距离换算比例
const DISTANCE_RATIO = 2;
// IOS 开始加载历史消息的 scrollTop
const IOS_START_LOADING_SCROLL_TOP = -10;
const eventListenerOptions = canUse('passiveListener') ? { passive: false } : false;

// 下拉刷新的状态值
export enum PullToLoadStatus {
  /** default status */
  PENDING = 'pending',
  /** 正在下拉 */
  PULLING = 'pulling',
  /** 下拉完成 */
  ACTIVE = 'active',
  /** 加载数据 */
  LOADING = 'loading'
}

export type PullToLoadProps = {
  id?: string;
  /** 是否禁用 */
  disabled?: boolean;
  className?: string;
  /** 获取新数据 */
  onLoad?: () => Promise<any>;
} & typeof PullToLoad.defaultProps & {
  agentStore: any;
  initStore: any;
};

export interface PullToLoadState {
  /** 当前下拉状态 */
  status: PullToLoadStatus;
  hasMore: boolean;
}

let firstRedraw = false;

@inject(getChildInject('agentStore'))
@inject(getChildInject('initStore'))
@observer
export default class PullToLoad extends React.Component<PullToLoadProps, PullToLoadState> {
  wrapperRef = createRef<HTMLDivElement>(); // document.getElementById('wrapper')
  contentRef = createRef<HTMLDivElement>(); // content ref 计算 loading 偏移

  startY = 0; // 开始滚动 Y 坐标
  events: any;
  isIosRebounding = false; // ios 是否正在回弹

  static defaultProps = {
    loadingDistance: DEFAULT_LOADING_DISTANCE,
    indicator: {
      [PullToLoadStatus.PENDING]: 'pending',
      [PullToLoadStatus.PULLING]: 'pulling',
      [PullToLoadStatus.LOADING]: 'loading',
      [PullToLoadStatus.ACTIVE]: 'active',
    },
  };

  constructor(props: PullToLoadProps) {
    super(props);
    this.state = {
      status: PullToLoadStatus.PENDING,
      hasMore: true,
    };
  }

  componentDidMount() {
    const { disabled } = this.props;

    if (disabled) { return; }

    this.addScrollListener();
  }

  componentDidUpdate(prevProps: PullToLoadProps) {
    if (prevProps.disabled !== this.props.disabled && !this.props.disabled) {
      this.addScrollListener();
    } else if (prevProps.disabled !== this.props.disabled && this.props.disabled) {
      this.removeScrollListener();
    }
  }

  componentWillUnmount() {
    this.removeScrollListener();
  }

  addScrollListener = () => {
    const wrapper = this.wrapperRef.current;
    this.events = {
      touchstart: this.touchStart,
      touchmove: this.touchMove,
      touchend: this.touchEnd,
      touchcancel: this.touchEnd,
      scroll: this.onScrollThrottle,
    };

    if (wrapper) {
      Object.keys(this.events).forEach(eventName => {
        wrapper.addEventListener(eventName, this.events[eventName], eventListenerOptions);
      });
    }
  };

  removeScrollListener = () => {
    const wrapper = this.wrapperRef.current;

    if (wrapper) {
      this.events && Object.keys(this.events).forEach(eventName => {
        wrapper.removeEventListener(eventName, this.events[eventName]);
      });
    }
  };

  touchStart = (e: TouchEvent) => {
    const { status } = this.state;

    this.startY = e.touches[0].clientY;

    if (status === PullToLoadStatus.PENDING) {
      this.setState({ status: PullToLoadStatus.PULLING });
    }
  };

  touchMove = throttle((e: TouchEvent) => {
    if (this.state.status === PullToLoadStatus.LOADING || this.state.status === PullToLoadStatus.ACTIVE) {
      e?.preventDefault?.();
    }
    const { loadingDistance } = this.props;
    const { status } = this.state;
    const endY = e?.touches[0]?.clientY;

    // 是否可以触发下拉加载
    if (!this.shouldStartMove(endY, status)) { return; }

    const dist = (endY - this.startY) / DISTANCE_RATIO;

    if (dist > 0) {
      if (e.cancelable) {
        e?.preventDefault?.();
      }
      e?.stopPropagation?.();

      // 展示出一半的loading
      const showLoading = dist >= loadingDistance / 2;

      // 设置 loading 的transform值
      this.setContentTranslateY(showLoading ? dist : 0);

      // 如果展示出了loading，将状态改为active，否则就还是pull
      this.setState({ status: showLoading ? PullToLoadStatus.ACTIVE : PullToLoadStatus.PULLING });
    }
  }, THROTTLE_INTERVAL);

  touchEnd = () => {
    const { status } = this.state;
    const { userAgent } = window.navigator;
    const isIos = isIosReg.test(userAgent);

    // 只有当状态是active，才会加载数据
    if (status === PullToLoadStatus.ACTIVE) {
      const { loadingDistance } = this.props;

      this.setState({ status: PullToLoadStatus.LOADING }, () => {
        this.setContentTranslateY(loadingDistance);
      });

      // IOS 回弹，只有在 scrollTop 不等于 0 时下拉页面才会获得负值的 scrollTop
      // 如果从 scrollTop 等于 0 的位置下拉，不会触发 scroll 事件
      if (isIos && this.wrapperRef.current?.scrollTop !== 0) {
        this.isIosRebounding = true;
      } else {
        // setTimeout(() => {
        //   // 当回弹完全结束时候再加载
        //   this.handleLoadMore();
        // }, 300);
        this.handleLoadMore();
      }
    }
  };

  handleLoadMore = () => {
    const { loadingDistance, onLoad } = this.props;

    try {
      const wrapper = this.wrapperRef.current;
      const wrapperScrollHeight = wrapper!.scrollHeight;

      onLoad!().then((res) => {
        const handleOffset = () => {
          // 渲染后的新高度 - 渲染前的旧高度 - loading的高度
          // 当前是否滚动了，如果用户滚动了，需要加上滚动的距离
          let scrollTop = wrapper!.scrollHeight - wrapperScrollHeight - loadingDistance;
          const currentScrollTop = wrapper!.scrollTop;

          if (currentScrollTop) {
            scrollTop += currentScrollTop;
          }

          if (!firstRedraw) {
            firstRedraw = true;
            const originDisplay = wrapper!.style.display ?? 'block';
            wrapper!.style.display = 'none';
            setTimeout(() => {
              wrapper!.style.display = originDisplay;
              this.scrollTo(scrollTop);
            }, 0);
          } else {
            this.scrollTo(scrollTop);
          }
        };

        handleOffset();

        setTimeout(() => {
          this.reset();
        }, 300);

        this.setState({ hasMore: res.has_more });

        // 如果没有更多了，就移除监听，显示‘没有更多’文案
        if (res && !res.has_more) {
          this.removeScrollListener();
        }
      });
    } catch (e) {
      this.reset();
    }
  };

  shouldStartMove(endY: number, status: PullToLoadStatus) {
    const wrapper = this.wrapperRef.current;
    // 当前滚动高度
    const scrollTop = wrapper!.scrollTop;

    // 向下拉 && 现在的状态不是在 loading && 显示出了 loading 图标
    return endY > this.startY && status !== PullToLoadStatus.LOADING && scrollTop <= 0;
  }

  setContentTranslateY(y: number) {
    const content = this.contentRef.current;

    if (content) {
      setTransform(content, `translate3d(0px, ${y}px, 0)`);
    }
  }

  reset() {
    this.setState({ status: PullToLoadStatus.PENDING });
    this.setContentTranslateY(0);
  }

  scrollTo = (y: number) => {
    const wrapper = this.wrapperRef.current;
    if (wrapper) {
      wrapper.scrollTo(0, y);
    }
  };

  onScroll = (e: any) => {
    const scrollTop = e.target.scrollTop;

    if (this.isIosRebounding && scrollTop > IOS_START_LOADING_SCROLL_TOP) {
      this.isIosRebounding = false;

      // setTimeout(() => {
      //   this.handleLoadMore();
      // }, 300);
      this.handleLoadMore();
    }
  };

  onScrollThrottle = throttleReplacement(this.onScroll, 200);

  render() {
    const { children, className, indicator, id, agentStore } = this.props;
    const { status, hasMore } = this.state;
    const { connectPremiumLiveChatDirectly, inAgent, inNewFlowBeforeAgent } = agentStore;

    const { enable: enableShortcuts } = applyChatbotConfig('shortcut', this.props) as {
      enable: boolean;
    };
    const { enable: enableInputArea } = applyChatbotConfig('inputArea', this.props) as {
      enable: boolean;
    };

    return (
      <div
        id={id}
        className={
          clsx(
            `pull-to-load ${isShortCutNewUI() ? 'pull-to-load_new_ui' : ''}`,
            className,
            { 'hide-bottom': connectPremiumLiveChatDirectly && inAgent || (!enableShortcuts && !enableInputArea) },
            { 'pull-to-load_in-before-agent': inNewFlowBeforeAgent && !inAgent },
          )
        }
        ref={this.wrapperRef}
      >
        <div
          className="pull-to-load__content"
          ref={this.contentRef}
        >
          <div
            key={status}
            className={clsx('pull-to-load__indicator', { 'has-more': hasMore })}
          >
            {
              indicator[status]
            }
          </div>
          {React.Children.only(children)}
        </div>
      </div>
    );
  }
}
